/**
 * A tabela densa — a peça que toda lista do sistema usa.
 *
 * Densa de propósito: quem trabalha aqui passa o dia varrendo duzentas linhas, e
 * altura de linha generosa custa contexto. 26 px por linha, cabeçalho fixo, zebra
 * discreta, e a seleção marcada com a cor da marca, não com um azul de sistema.
 */

import { useRef } from "react"
import { usarRolarAteRecente } from "./ordemDaLista"
import type { CSSProperties, ReactNode } from "react"

export type Coluna<T> = {
  chave: string
  rotulo: string
  /** Largura fixa em px. Sem largura, a coluna divide o espaço que sobra. */
  largura?: number
  minimo?: number
  alinhamento?: "esquerda" | "direita" | "centro"
  /** Identificador vai em monoespaçado — regra 8 da padronização. */
  mono?: boolean
  render: (item: T) => ReactNode
}

const ALTURA_LINHA = 26
const ALTURA_CABECA = 30

function alinhar(a?: Coluna<unknown>["alinhamento"]): CSSProperties["textAlign"] {
  return a === "direita" ? "right" : a === "centro" ? "center" : "left"
}

export function Tabela<T>({
  colunas,
  itens,
  chaveDe,
  selecionada,
  aoSelecionar,
  vazio,
  marcados,
  aoMarcar,
  aoAbrir,
  recente,
}: {
  colunas: Coluna<T>[]
  itens: T[]
  chaveDe: (item: T) => string | number
  selecionada?: string | number | null
  aoSelecionar?: (item: T) => void
  vazio?: ReactNode
  /** Quando presente, a tabela ganha coluna de marcação para a ação em lote. */
  marcados?: Set<number>
  aoMarcar?: (ids: number[], marcar: boolean) => void
  /**
   * Abrir é diferente de selecionar. Clique seleciona (é o que a ação em lote e
   * a barra de estado usam); Enter e duplo clique abrem. Sem `aoAbrir` o Enter
   * continua caindo em `aoSelecionar`, que é o que as telas antigas esperam.
   */
  aoAbrir?: (item: T) => void
  /**
   * A chave da linha que acabou de ser incluída: ela fica realçada e a tabela
   * rola até ela. Ver `kit/ordemDaLista`.
   */
  recente?: string | number | null
}) {
  const temMarcacao = marcados != null && aoMarcar != null
  // Guarda a última linha marcada para o Shift+clique pegar o intervalo.
  const ancora = useRef<number | null>(null)
  const rolagem = useRef<HTMLDivElement>(null)
  usarRolarAteRecente(rolagem, recente == null ? null : Number(recente), itens)

  const grade = (temMarcacao ? "30px " : "") + colunas
    .map((c) => (c.largura ? `${c.largura}px` : `minmax(${c.minimo ?? 120}px, 1fr)`))
    .join(" ")

  const idsDaPagina = itens.map((i) => Number(chaveDe(i)))
  const todosMarcados = temMarcacao && idsDaPagina.length > 0 && idsDaPagina.every((id) => marcados.has(id))

  function marcarLinha(id: number, ev: React.MouseEvent | React.KeyboardEvent) {
    if (!temMarcacao) return
    const marcar = !marcados.has(id)

    // Shift+clique marca do último marcado até aqui — é como se seleciona
    // cinquenta linhas sem cinquenta cliques.
    if ("shiftKey" in ev && ev.shiftKey && ancora.current != null) {
      const a = idsDaPagina.indexOf(ancora.current)
      const b = idsDaPagina.indexOf(id)
      if (a >= 0 && b >= 0) {
        const [ini, fim] = a < b ? [a, b] : [b, a]
        aoMarcar(idsDaPagina.slice(ini, fim + 1), true)
        return
      }
    }
    ancora.current = id
    aoMarcar([id], marcar)
  }

  return (
    <div ref={rolagem} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
      <div
        role="table"
        aria-rowcount={itens.length}
        style={{ minWidth: "fit-content", display: "flex", flexDirection: "column" }}
      >
        <div
          role="row"
          style={{
            position: "sticky", top: 0, zIndex: 1,
            display: "grid", gridTemplateColumns: grade, alignItems: "center",
            height: ALTURA_CABECA, background: "var(--surface2)",
            borderBottom: "1px solid var(--line2)",
          }}
        >
          {temMarcacao && (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Caixa
                marcada={todosMarcados}
                rotulo={todosMarcados ? "Desmarcar a página" : "Marcar a página inteira"}
                aoClicar={() => aoMarcar(idsDaPagina, !todosMarcados)}
              />
            </span>
          )}
          {colunas.map((c) => (
            <span
              key={c.chave}
              role="columnheader"
              style={{
                padding: "0 10px", fontSize: 10, fontWeight: 600, letterSpacing: ".07em",
                textTransform: "uppercase", color: "var(--txt3)", textAlign: alinhar(c.alinhamento),
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {c.rotulo}
            </span>
          ))}
        </div>

        {itens.length === 0 && vazio}

        {itens.map((item, i) => {
          const chave = chaveDe(item)
          const ativa = selecionada != null && selecionada === chave
          const marcada = temMarcacao && marcados.has(Number(chave))
          return (
            <div
              key={chave}
              role="row"
              tabIndex={0}
              aria-selected={ativa}
              data-recem-incluido={recente != null && recente === chave ? "" : undefined}
              onClick={() => aoSelecionar?.(item)}
              onDoubleClick={() => aoAbrir?.(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); (aoAbrir ?? aoSelecionar)?.(item) }
                // Espaço marca a linha: seleção sem tirar a mão do teclado.
                if (e.key === " ") { e.preventDefault(); marcarLinha(Number(chave), e) }
                if (e.key === "ArrowDown") { e.preventDefault(); irmao(e.currentTarget, 1) }
                if (e.key === "ArrowUp") { e.preventDefault(); irmao(e.currentTarget, -1) }
              }}
              style={{
                display: "grid", gridTemplateColumns: grade, alignItems: "center",
                height: ALTURA_LINHA, cursor: aoSelecionar ? "pointer" : "default",
                // Marcada e selecionada não podem se parecer: a marcada entra na ação
                // em lote, a selecionada só é a linha do cursor. Com o mesmo fundo, a
                // linha clicada parecia marcada sem estar — e a ação em lote não a via.
                background: marcada
                  ? "var(--brand-soft)"
                  : ativa ? "var(--surface3)" : i % 2 === 1 ? "var(--surface2)" : "transparent",
                boxShadow: ativa ? "inset 2px 0 0 var(--brand)" : undefined,
                color: marcada ? "var(--brand-on-soft)" : undefined,
              }}
            >
              {temMarcacao && (
                // A célula inteira marca, não só o quadradinho de 14 px: é alvo de
                // clique que se acerta sem mirar.
                <span
                  style={{ alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onClick={(ev) => { ev.stopPropagation(); marcarLinha(Number(chave), ev) }}
                >
                  <Caixa marcada={marcada} rotulo="Marcar a linha"
                         aoClicar={(ev) => marcarLinha(Number(chave), ev)} />
                </span>
              )}
              {colunas.map((c) => (
                <span
                  key={c.chave}
                  role="cell"
                  style={{
                    padding: "0 10px", fontSize: 12,
                    fontFamily: c.mono ? "var(--fm)" : undefined,
                    textAlign: alinhar(c.alinhamento),
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}
                >
                  {c.render(item)}
                </span>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Seta para cima e para baixo andam de linha em linha, sem tirar a mão do teclado. */
function irmao(atual: HTMLElement, direcao: 1 | -1) {
  const alvo = direcao === 1 ? atual.nextElementSibling : atual.previousElementSibling
  if (alvo instanceof HTMLElement && alvo.getAttribute("role") === "row") {
    alvo.focus()
    alvo.scrollIntoView({ block: "nearest" })
  }
}

/** Bloco central para "carregando", "deu erro" e "não há nada aqui". */
export function LinhaDeAviso({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: "56px 24px", display: "flex", flexDirection: "column",
        alignItems: "center", gap: 8, textAlign: "center", color: "var(--txt2)", fontSize: 13,
      }}
    >
      {children}
    </div>
  )
}

/**
 * O quadradinho de marcar. Recebe o evento para o Shift+clique chegar à tabela:
 * antes ele parava o clique e chamava uma função vazia, e clicar bem em cima da
 * caixa não marcava nada — só a borda em volta dela marcava.
 */
function Caixa({ marcada, rotulo, aoClicar }: {
  marcada: boolean; rotulo: string; aoClicar: (ev: React.MouseEvent) => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={marcada}
      aria-label={rotulo}
      onClick={(e) => { e.stopPropagation(); aoClicar(e) }}
      style={{
        width: 14, height: 14, borderRadius: 4,
        border: `1.5px solid ${marcada ? "var(--brand)" : "var(--line2)"}`,
        background: marcada ? "var(--brand)" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 14px",
      }}
    >
      {marcada && (
        <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true"
             style={{ fill: "none", stroke: "#FFFFFF", strokeWidth: 3.4, strokeLinecap: "round", strokeLinejoin: "round" }}>
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
      )}
    </button>
  )
}
